import { defineComponent as _defineComponent } from 'vue'
import { computed, reactive, watch } from 'vue';
import { useToaster } from '@/composables/toaster';
import CInput from '@/components/shared/CInput';
import CSelect, { valueReducer } from '@/components/shared/CSelect';
import type { CanvasEx } from '@/typings/canvas';
import type {
    QrCornersDotType,
    QrCornersSquareType,
    QrDotsType,
    QrShape,
    QrStyleOptions,
    VectorPlaceholderElement
} from '@/typings/customilyElements/canvasElements';
import {
    DEFAULT_QR_STYLE_OPTIONS,
    QR_CORNERS_DOT_TYPES,
    QR_CORNERS_SQUARE_TYPES,
    QR_DOTS_TYPES,
    QR_SHAPES,
    isStyledQrConfig,
    updateStyledQrOptions
} from '@/utils/qrCodeUtils';


export default /*#__PURE__*/_defineComponent({
  __name: 'StyleProperties',
  props: {
    canvas: null,
    vectorElement: null,
    editMode: { type: Boolean }
  },
  setup(__props: any) {

const props = __props as {
    canvas: CanvasEx;
    vectorElement?: VectorPlaceholderElement;
    editMode?: boolean;
};



// The service renders styled QRs on a 1000x1000 canvas and the margin is the px quiet zone
//  inside it, so anything below ~20 is barely visible.
const MIN_MARGIN = 0;
const MAX_MARGIN = 200;

const model = reactive({
    shape: DEFAULT_QR_STYLE_OPTIONS.shape as QrShape,
    margin: DEFAULT_QR_STYLE_OPTIONS.margin,
    dotsType: DEFAULT_QR_STYLE_OPTIONS.dotsOptions.type as QrDotsType,
    cornersSquareType: DEFAULT_QR_STYLE_OPTIONS.cornersSquareOptions.type as QrCornersSquareType,
    cornersDotType: DEFAULT_QR_STYLE_OPTIONS.cornersDotOptions.type as QrCornersDotType
});

watch(
    () => props.vectorElement,
    (element) => {
        if (!element || !isStyledQrConfig(element.qrConfig)) return;

        const options = element.qrConfig.options;
        model.shape = options.shape;
        model.margin = options.margin;
        model.dotsType = options.dotsOptions.type;
        model.cornersSquareType = options.cornersSquareOptions.type;
        model.cornersDotType = options.cornersDotOptions.type;
    },
    { immediate: true }
);

const disabled = computed(() => !!props.vectorElement?.locked);

const currentOptions = computed<QrStyleOptions | undefined>(() =>
    isStyledQrConfig(props.vectorElement?.qrConfig) ? props.vectorElement!.qrConfig.options : undefined
);

const toaster = useToaster();
const applyOptions = async (options: Partial<QrStyleOptions>) => {
    try {
        await updateStyledQrOptions(props.vectorElement, props.canvas, options, props.editMode);
    } catch (error) {
        console.error('Error updating QR code style:', error);
        toaster.error('An error occurred while generating the QR code');
    }
};

const onShapeSelected = () => applyOptions({ shape: model.shape });

const onMarginChanged = () => {
    model.margin = Math.max(MIN_MARGIN, Math.min(MAX_MARGIN, Number(model.margin) || 0));
    applyOptions({ margin: model.margin });
};

const onDotsTypeSelected = () => {
    const dotsOptions = currentOptions.value?.dotsOptions;
    if (!dotsOptions) return;
    applyOptions({ dotsOptions: { ...dotsOptions, type: model.dotsType } });
};

const onCornersSquareTypeSelected = () => {
    const cornersSquareOptions = currentOptions.value?.cornersSquareOptions;
    if (!cornersSquareOptions) return;
    applyOptions({ cornersSquareOptions: { ...cornersSquareOptions, type: model.cornersSquareType } });
};

const onCornersDotTypeSelected = () => {
    const cornersDotOptions = currentOptions.value?.cornersDotOptions;
    if (!cornersDotOptions) return;
    applyOptions({ cornersDotOptions: { ...cornersDotOptions, type: model.cornersDotType } });
};

return { __sfc: true,props, MIN_MARGIN, MAX_MARGIN, model, disabled, currentOptions, toaster, applyOptions, onShapeSelected, onMarginChanged, onDotsTypeSelected, onCornersSquareTypeSelected, onCornersDotTypeSelected, CInput, CSelect, valueReducer, QR_CORNERS_DOT_TYPES, QR_CORNERS_SQUARE_TYPES, QR_DOTS_TYPES, QR_SHAPES }
}

})